import { useRef, useState, type ComponentProps, type FormEvent, type ReactNode } from 'react'
import { Alert, Button, Checkbox, Label, ListBox, Modal, Select, Tooltip } from '@heroui/react'
import { accepts, pickerNote } from '../lib/filePicker'
import { Icon } from '../ui/icons'
import { useStaffText } from '../ui/StaffText'
import '../styles/studio.css'

/** The Designs editor's HeroUI pieces: one select, one dialog and one notice for every panel. */

export interface StudioOption {
  value: string
  label: string
  disabled?: boolean
  /** Draws the option in this face (the font choice shows each font in itself). */
  fontFamily?: string
}

// '' is a value like any other here; a list box key may not be empty.
const keyOf = (value: string) => `o:${value}`

export function StudioSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  isDisabled,
  className,
  'aria-label': ariaLabel,
  'aria-labelledby': labelledBy,
}: {
  /** Drawn above the trigger and naming it. */
  label?: ReactNode
  value: string
  onChange: (value: string) => void
  options: readonly StudioOption[]
  placeholder?: string
  isDisabled?: boolean
  className?: string
  'aria-label'?: string
  'aria-labelledby'?: string
}) {
  const { say } = useStaffText()
  const chosen = options.find((o) => o.value === value)
  const empty = placeholder ?? say('common.choose')
  return (
    <Select
      aria-label={ariaLabel}
      aria-labelledby={labelledBy}
      className={className ? `studio-select ${className}` : 'studio-select'}
      variant="secondary"
      fullWidth
      isDisabled={isDisabled}
      placeholder={empty}
      value={chosen ? keyOf(chosen.value) : null}
      disabledKeys={options.filter((o) => o.disabled).map((o) => keyOf(o.value))}
      onChange={(key) => {
        if (key == null) return
        const next = String(key).slice(2)
        if (next !== value) onChange(next)
      }}
    >
      {label !== undefined && <Label>{label}</Label>}
      <Select.Trigger>
        {/* Drawn from the options, so the closed select says its value before the list is built. */}
        <Select.Value>
          {() => (chosen ? <span style={chosen.fontFamily ? { fontFamily: chosen.fontFamily } : undefined}>{chosen.label}</span> : empty)}
        </Select.Value>
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {options.map((o) => (
            <ListBox.Item key={keyOf(o.value)} id={keyOf(o.value)} textValue={o.label} style={o.fontFamily ? { fontFamily: o.fontFamily } : undefined}>
              {o.label}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  )
}

/**
 * A dialog. Escape, the backdrop and the close button close it; with guard, once
 * something was typed, Escape or a stray click asks before throwing it away.
 * onSubmit makes body and footer one form, so Enter in a field submits.
 */
export function StudioModal({
  title,
  sub,
  size = 'md',
  placement = 'auto',
  guard = false,
  onClose,
  onSubmit,
  footer,
  children,
}: {
  title: string
  sub?: string
  size?: 'sm' | 'md' | 'lg'
  /** top for a dialog whose height changes as it is filled in, so it does not jump. */
  placement?: 'auto' | 'top'
  guard?: boolean
  onClose: () => void
  onSubmit?: () => void
  footer?: ReactNode
  children: ReactNode
}) {
  const { say } = useStaffText()
  const dirty = useRef(false)
  const [asking, setAsking] = useState(false)
  const request = () => (guard && dirty.current ? setAsking(true) : onClose())
  const touched = () => {
    dirty.current = true
  }
  const body = (
    <>
      <Modal.Body className="studio-modal__body" onInput={touched} onChange={touched}>
        {children}
      </Modal.Body>
      {asking ? (
        <Modal.Footer className="studio-modal__foot" role="alertdialog" aria-label={say('ui.unsaved')}>
          <strong className="studio-modal__ask">{say('ui.discardAsk')}</strong>
          <Button variant="tertiary" size="sm" autoFocus onPress={() => setAsking(false)}>
            {say('ui.keepEditing')}
          </Button>
          <Button variant="danger" size="sm" onPress={onClose}>
            {say('ui.discard')}
          </Button>
        </Modal.Footer>
      ) : (
        footer && <Modal.Footer className="studio-modal__foot">{footer}</Modal.Footer>
      )}
    </>
  )
  return (
    <Modal.Backdrop isOpen onOpenChange={(open) => !open && request()}>
      <Modal.Container size={size} placement={placement} scroll="inside">
        <Modal.Dialog className="studio-modal" aria-label={title}>
          <Modal.CloseTrigger aria-label={say('ui.close')} />
          <Modal.Header>
            <Modal.Heading>{title}</Modal.Heading>
            {sub && <p className="studio-modal__sub">{sub}</p>}
          </Modal.Header>
          {onSubmit ? (
            <form
              className="studio-modal__form"
              onSubmit={(e: FormEvent) => {
                e.preventDefault()
                onSubmit()
              }}
            >
              {body}
            </form>
          ) : (
            body
          )}
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}

/** A line the editor says about what just happened: HeroUI's Alert. */
export function Notice({
  tone = 'danger',
  role,
  className,
  children,
}: {
  tone?: 'default' | 'accent' | 'warning' | 'danger'
  role?: 'alert' | 'status'
  className?: string
  children: ReactNode
}) {
  return (
    <Alert status={tone} role={role} className={className ? `studio-notice ${className}` : 'studio-notice'}>
      <Alert.Indicator />
      <Alert.Content>
        <Alert.Title>{children}</Alert.Title>
      </Alert.Content>
    </Alert>
  )
}

/**
 * ui/FilePicker on a HeroUI button: the browser's file input stays, hidden, for its
 * dialog. A file the dialog's "All files" let through is refused beside the button.
 */
export function StudioFilePicker({
  accept,
  disabled,
  label,
  onFile,
  tip,
}: {
  accept?: string
  disabled?: boolean
  label?: string
  onFile: (file: File) => void
  /** What the picker takes, as a tooltip. */
  tip?: string
}) {
  const { say } = useStaffText()
  const input = useRef<HTMLInputElement>(null)
  const [lastName, setLastName] = useState<string | undefined>(undefined)
  const [refused, setRefused] = useState<string | undefined>(undefined)
  const note = pickerNote(lastName, refused, {
    noneChosen: say('common.noFileChosen'),
    notAccepted: (name) => say('common.fileNotAccepted', { name }),
  })
  return (
    <span className="studio-file" title={tip}>
      <input
        ref={input}
        type="file"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        accept={accept}
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0]
          // Emptied whatever happens, so choosing the same file again is a change.
          e.target.value = ''
          if (!file) return
          if (!accepts(accept, file)) {
            setRefused(file.name)
            return
          }
          setRefused(undefined)
          setLastName(file.name)
          onFile(file)
        }}
      />
      <Button variant="secondary" size="sm" isDisabled={disabled} onPress={() => input.current?.click()}>
        <Icon name="upload" size={14} />
        {label ?? say('common.chooseFile')}
      </Button>
      <span className="studio-file__name" data-refused={note.refused || undefined} role="status">
        {note.text}
      </span>
    </span>
  )
}

/** An icon-only button that names itself on hover, as its title did before HeroUI. */
export function IconButton({ tip, children, ...props }: { tip?: string; 'aria-label': string } & Omit<ComponentProps<typeof Button>, 'isIconOnly'>) {
  return (
    <Tooltip delay={500} closeDelay={0}>
      <Button isIconOnly variant="ghost" size="sm" {...props}>
        {children}
      </Button>
      <Tooltip.Content>{tip ?? props['aria-label']}</Tooltip.Content>
    </Tooltip>
  )
}

/** One choice of several that may all be on: HeroUI's checkbox (secondary: its box shows on a white card). */
export function Tick({ label, checked, onChange }: { label: string; checked: boolean; onChange: (on: boolean) => void }) {
  return (
    <Checkbox className="studio-check" variant="secondary" isSelected={checked} onChange={onChange}>
      <Checkbox.Content>
        <Checkbox.Control>
          <Checkbox.Indicator />
        </Checkbox.Control>
        {label}
      </Checkbox.Content>
    </Checkbox>
  )
}
